import type { Point } from '../doc/geometry';
import type { NewItem } from '../doc/schema';
import { viewCenter, type Ctx } from './actions';
import { storeFileAsset, storeImageAsset } from './assets/localAssets';
import { DEFAULT_FILE_SIZE } from '../doc/schema';
import { enqueueUpload } from '../sync/uploadQueue';

/** Default on-board size for a freshly imported image (world units, longest side). */
const IMPORT_MAX_SIDE = 480;

export function isImageFile(f: File): boolean {
  return f.type.startsWith('image/');
}

/**
 * Imports files as image items, laid out in a loose grid around `at` (or the view centre).
 * Variants are generated in the browser before the item appears.
 */
export async function importImageFiles(ctx: Ctx, files: File[], at?: Point): Promise<string[]> {
  const images = files.filter(isImageFile);
  const others = files.filter((f) => !isImageFile(f));
  const origin = at ?? viewCenter(ctx.pane);
  const attached = await importAttachments(ctx, others, origin, images.length ? 1 : 0);
  if (!images.length) return attached;
  const cols = Math.ceil(Math.sqrt(images.length));
  const gap = 24;
  const cell = IMPORT_MAX_SIDE + gap;
  const gridW = cols * cell - gap;
  const rows = Math.ceil(images.length / cols);
  const gridH = rows * cell - gap;

  const created: string[] = [];
  for (let i = 0; i < images.length; i++) {
    const file = images[i]!;
    let meta;
    try {
      meta = await storeImageAsset(file);
    } catch (e) {
      console.warn(`Could not import ${file.name}:`, e);
      continue;
    }
    const scale = Math.min(1, IMPORT_MAX_SIDE / Math.max(meta.width, meta.height));
    const w = Math.round(meta.width * scale);
    const h = Math.round(meta.height * scale);
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = origin.x - gridW / 2 + col * cell + IMPORT_MAX_SIDE / 2;
    const cy = origin.y - gridH / 2 + row * cell + IMPORT_MAX_SIDE / 2;
    const input: NewItem = {
      type: 'image',
      x: Math.round(cx - w / 2),
      y: Math.round(cy - h / 2),
      w,
      h,
      props: { assetId: meta.id, naturalW: meta.width, naturalH: meta.height, opacity: 1 },
    };
    created.push(ctx.board.createItem(input));
    ctx.pane.getState().select([...attached, ...created]);
    if (ctx.board.remote) void enqueueUpload(ctx.board.boardId, meta.id);
  }
  return [...attached, ...created];
}

/** Non-image files become attachment chips stacked below the drop point. */
async function importAttachments(ctx: Ctx, files: File[], origin: Point, rowOffset: number): Promise<string[]> {
  const ids: string[] = [];
  const { w, h } = DEFAULT_FILE_SIZE;
  for (let i = 0; i < files.length; i++) {
    const file = files[i]!;
    let meta;
    try {
      meta = await storeFileAsset(file);
    } catch (e) {
      console.warn(`Could not attach ${file.name}:`, e);
      continue;
    }
    ids.push(
      ctx.board.createItem({
        type: 'file',
        x: Math.round(origin.x - w / 2),
        y: Math.round(origin.y + rowOffset * 300 + i * (h + 12)),
        w,
        h,
        props: { assetId: meta.id, name: meta.name, size: meta.bytes, mime: meta.mime },
      }),
    );
    if (ctx.board.remote) void enqueueUpload(ctx.board.boardId, meta.id);
  }
  return ids;
}

export function pickImageFiles(): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = () => resolve(Array.from(input.files ?? []));
    input.oncancel = () => resolve([]);
    input.click();
  });
}
